import React, { useState, useEffect } from 'react';

import { Container } from '../../../grid';
import Button from '../../molecules/button';
import EventCreate from '../event/create';
import PatientEventNote from '../event/patient-note';
import Callout from '../callout';

import { AppKey } from '../../../libs/apps';
import { useGetPatientEvents, IEvent } from '../../../libs/event';
import { useMetaData } from '../../../contexts/metadata';

function generateNotes(
  events: IEvent[],
  isLoading: boolean,
  appKey: AppKey
): JSX.Element {
  if (events.length > 0) {
    const notes = [];
    events.map((event) => {
      notes.push(<PatientEventNote key={event.id} data={event} />);
    });
    return <>{notes}</>;
  } else {
    if (isLoading) {
      return <p>Loading Event Notes...</p>;
    } else {
      if (appKey) {
        return <p>No Event Notes have been created for this program yet.</p>;
      } else {
        return <p>No Event Notes exist yet.</p>;
      }
    }
  }
}

function generateFooter(
  events: IEvent[],
  isLoading: boolean,
  hasMore: boolean,
  appKey: AppKey,
  handleMoreClick: () => void
): JSX.Element {
  if (events.length <= 0) {
    return null;
  }
  if (isLoading) {
    return <p>Loading More Event Notes...</p>;
  } else {
    if (hasMore) {
      return <Button onClick={handleMoreClick}>Load More Event Notes</Button>;
    } else {
      if (appKey) {
        return (
          <Callout>All Events Notes for this program have been loaded.</Callout>
        );
      } else {
        return <Callout>All Events Notes have been loaded.</Callout>;
      }
    }
  }
}

interface PatientEventsProps {
  appKey?: AppKey;
  appKeys?: string[];
  patientId: number;
}
const PatientEvents: React.FC<PatientEventsProps> = ({
  appKey,
  appKeys,
  patientId
}): JSX.Element => {
  const [page, setPage] = useState(0);
  const { isLoading, addNewEvent, events, hasMore } = useGetPatientEvents({
    app: appKey,
    apps: appKeys,
    patientId,
    limit: 10,
    page
  });
  const { fetchContacts, fetchUsers, isReadOnly } = useMetaData();
  useEffect(() => {
    if (events) {
      const contactIDs: number[] = events
        .map((event) => event.contactId)
        .filter((id) => id != null);
      fetchContacts(contactIDs);
      const userIDs: number[] = events
        .map((event) => event.userId)
        .filter((id) => id != null);
      fetchUsers(userIDs);
    }
  }, [events]);
  const handleMoreClick = () => {
    setPage(page + 1);
  };
  const notes = generateNotes(events, isLoading, appKey);
  const footer = generateFooter(
    events,
    isLoading,
    hasMore,
    appKey,
    handleMoreClick
  );
  return (
    <>
      {!isReadOnly && (
        <EventCreate
          appKey={appKey}
          patientId={patientId}
          handleNewEvent={addNewEvent}
        />
      )}
      <Container fluid className="pt-4 mb-4 px-8-important bg-light-grey">
        {notes}
      </Container>
      {footer}
    </>
  );
};

export default PatientEvents;
